import { useState, useMemo } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

/**
 * Searchable multi-select popover. Trigger shows selected count + a chip preview.
 * Body has a search input and a scrollable checkable list.
 *
 * @param {object} props
 * @param {Array<{label:string,value:string}>} props.options
 * @param {string[]} props.values
 * @param {(next:string[])=>void} props.onChange
 * @param {string} [props.placeholder]  - shown when nothing is selected
 * @param {string} [props.testid]
 * @param {string} [props.searchPlaceholder]
 */
export default function MultiSelectPopover({
  options,
  values,
  onChange,
  placeholder = "Select…",
  testid = "multi-select",
  searchPlaceholder = "Search…",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [query, options]);

  const toggle = (v) => {
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const labelFor = (v) => options.find((o) => o.value === v)?.label ?? v;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testid}-trigger`}
          className="mt-1 flex w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-1.5 text-left text-sm transition-colors hover:bg-surface-hover focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <span className="flex min-w-0 flex-1 items-center gap-1">
            {values.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : values.length <= 2 ? (
              values.map((v) => (
                <Badge key={v} variant="secondary" className="max-w-[140px] truncate text-[10px]">
                  {labelFor(v)}
                </Badge>
              ))
            ) : (
              <>
                <Badge variant="secondary" className="max-w-[120px] truncate text-[10px]">
                  {labelFor(values[0])}
                </Badge>
                <Badge variant="secondary" className="text-[10px]">+{values.length - 1}</Badge>
              </>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
            {values.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                onClick={clearAll}
                onKeyDown={(e) => { if (e.key === "Enter") clearAll(e); }}
                className="rounded p-0.5 hover:bg-surface-hover"
                data-testid={`${testid}-clear`}
                aria-label="Clear selection"
              >
                <X className="h-3 w-3" />
              </span>
            )}
            <ChevronDown className="h-3.5 w-3.5" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <div className="border-b border-border p-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-8 text-sm"
            data-testid={`${testid}-search`}
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1" data-testid={`${testid}-list`}>
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              No matches.
            </div>
          ) : (
            filtered.map((o) => {
              const active = values.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o.value)}
                  data-testid={`${testid}-option-${o.value}`}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-surface-hover ${
                    active ? "text-foreground" : "text-foreground/85"
                  }`}
                >
                  <span className="truncate">{o.label}</span>
                  {active && <Check className="h-3.5 w-3.5 text-violet-text" />}
                </button>
              );
            })
          )}
        </div>
        {values.length > 0 && (
          <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs">
            <span className="text-muted-foreground">{values.length} selected</span>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-violet-text hover:underline"
              data-testid={`${testid}-clear-footer`}
            >
              Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
